/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The top of a detail screen: where you are, the way back, and what to do.
 *
 * It replaces the shell's own bar on these routes rather than sitting under
 * it. The shell's bar spends its 56px on the product's name and your avatar
 * — true on every screen, and therefore useless for telling one from another
 * — while the record's own name and its actions were down in the scrolling
 * content, which on a listing put "Editar" 741px down a 702px screen.
 *
 * Three actions at most, and that is a ceiling rather than a target: past
 * three, a 400px row makes each target narrower than the thumb pressing it.
 * Anything further belongs in an overflow menu, which counts as one.
 */

import type { ReactNode } from 'react';
import { ArrowLeftIcon } from '@radix-ui/react-icons';
import { Text, UnstyledButton } from '@/shared/ui';

/** The bar's height. The screen reserves it by being a flex column. */
export const DETAIL_HEADER_HEIGHT = 52;

interface MobileDetailHeaderProps {
  /** The short name of the record — a code, a person, not a sentence. */
  title: string;
  /** Returns to the list, which restores where it was. */
  onBack: () => void;
  backLabel: string;
  /**
   * Up to three icon controls, each already a 44px target. They are rendered
   * as given: this header does not know what a listing's actions are, and
   * should not have to.
   */
  actions?: ReactNode;
  /** Appears once the content has scrolled under the bar. */
  scrolled?: boolean;
}

export const MobileDetailHeader: React.FC<MobileDetailHeaderProps> = ({
  title, onBack, backLabel, actions, scrolled = false,
}) => (
  <header
    data-testid="mobile-detail-header"
    className="relative z-10 flex shrink-0 items-center gap-(--space-1)"
    style={{
      height: DETAIL_HEADER_HEIGHT,
      padding: '0 var(--space-2)',
      background: 'var(--surface-primary)',
      /*
       * Both states carry the same two layers, the resting one transparent,
       * so the browser interpolates one value into another rather than
       * padding a shorter list and arriving in two steps.
       */
      boxShadow: scrolled
        ? '0 1px 0 var(--border-subtle), 0 6px 14px -10px rgba(26,26,26,0.25)'
        : '0 1px 0 transparent, 0 6px 14px -10px transparent',
      transition: 'box-shadow 150ms ease',
    }}
  >
    <UnstyledButton
      type="button"
      data-testid="mobile-detail-back"
      aria-label={backLabel}
      onClick={onBack}
      className="flex shrink-0 items-center justify-center rounded-(--radius-md) text-[var(--text-primary)]"
      style={{ width: 44, height: 44 }}
    >
      <ArrowLeftIcon width={18} height={18} />
    </UnstyledButton>

    <Text
      component="h1"
      data-testid="mobile-detail-title"
      className="min-w-0 flex-1 truncate"
      style={{
        margin: 0,
        fontSize: 'var(--text-body-sm)',
        fontWeight: 'var(--font-semibold)' as unknown as number,
        color: 'var(--text-primary)',
      }}
    >
      {title}
    </Text>

    {actions && (
      <div className="flex shrink-0 items-center" style={{ gap: 2 }}>
        {actions}
      </div>
    )}
  </header>
);

/**
 * One 44px icon control for the header's action slot.
 *
 * Exported so the three detail screens cannot each invent their own size:
 * a row where two actions are 44px and the third is 38 reads as a mistake
 * long before anybody works out which one is wrong.
 */
export const MobileDetailAction: React.FC<{
  label: string;
  onClick: () => void;
  testId?: string;
  children: ReactNode;
}> = ({ label, onClick, testId, children }) => (
  <UnstyledButton
    type="button"
    aria-label={label}
    data-testid={testId}
    onClick={onClick}
    className="flex items-center justify-center rounded-(--radius-md) text-[var(--text-secondary)] hover:bg-[var(--surface-card)]"
    style={{ width: 44, height: 44 }}
  >
    {children}
  </UnstyledButton>
);
